"use client";

import { updateUserLanguage } from "@features/account/utils/updateUserLanguage";
import { CheckIcon } from "@heroicons/react/24/outline";
import { Avatar, Button, Flex, Popover, Text } from "@radix-ui/themes";
import { WhitelabelConfig } from "@types";
import { useSupabaseSession } from "@utils/hooks";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { mutate } from "swr";

interface Props {
  language: WhitelabelConfig["website"]["language"];
  disabled?: boolean;
}

export default function LocaleSwitcher({ language, disabled = false }: Props) {
  const t = useTranslations("LocaleSwitcher");
  const [locale, setLocale] = useState(useLocale());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const session = useSupabaseSession();

  async function onLanguageChange(nextLocale: string) {
    setLocale(nextLocale);
    if (session && session.access_token) {
      setLoading(true);
      try {
        await updateUserLanguage(nextLocale, session.access_token);
      } catch (error) {
        console.error({ error });
      }
    }

    const days = 30;
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `NEXT_LOCALE=${nextLocale};expires=${date.toUTCString()};path=/`;
    await mutate(() => true, undefined, { revalidate: true });
    setLoading(false);
    router.refresh();
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger>
        <Button
          variant="surface"
          color="gray"
          disabled={disabled || loading}
          loading={loading}
        >
          {locale.toUpperCase()}
        </Button>
      </Popover.Trigger>
      <Popover.Content minWidth="12rem" size="1">
        <Flex display="flex" direction="column" gap="1rem" align="start">
          {language?.supportedLanguages.map((cur) => (
            <button
              key={cur}
              onClick={() => {
                onLanguageChange(cur);
                setOpen(false);
              }}
              className="group w-full"
            >
              <Flex
                display="flex"
                gap="0.5rem"
                align="center"
                justify="between"
                px="0.5rem"
                py="0.375rem"
                width="100%"
              >
                <Flex
                  display="flex"
                  direction="row"
                  gap="0.5rem"
                  align="center"
                >
                  <Avatar
                    size="2"
                    fallback={cur.toUpperCase()}
                    className="group-hover:[&>span]:text-[color:var(--accent-10)] [&>span]:transition-colors [&>span]:duration-300 [&>span]:ease-in-out"
                  />
                  <Text
                    as="span"
                    weight="medium"
                    className="group-hover:text-[color:var(--accent-11)] transition-colors duration-300 ease-in-out"
                  >
                    {t("locale", { locale: cur })}
                  </Text>
                </Flex>
                {locale === cur ? (
                  <CheckIcon className="stroke-2 h-5 w-5 text-[color:var(--accent-11)]" />
                ) : (
                  <></>
                )}
              </Flex>
            </button>
          ))}
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
